import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { toTestIdSlug } from '@utils/testUtils';
import { getOverlayBounds } from '@utils/contentBounds.ts';
import { Z_LAYERS } from '@styles/zLayers.ts';

import SvgChevron from '@icons/chevron-thin.svg?react';
import SvgPlus from '@icons/plus.svg?react';
import SvgClose from '@icons/close.svg?react';
import SvgSelected from '@icons/check-circle.svg?react';

export interface RingilDropdownItem {
  id: string;
  label: string;
  // Optional styled rendering of the label. Renders in place of `label` text;
  // `label` is still used for search filtering and test ids.
  labelNode?: ReactNode;
  subLabel?: ReactNode;
  icon?: ReactNode;
  // When true the row is shown greyed-out and cannot be selected/toggled (nor picked by "Select all").
  // Already-selected disabled items keep their chip and can still be removed via the chip.
  disabled?: boolean;
  // Explanatory text rendered under a disabled row (why it can't be selected).
  disabledReason?: ReactNode;
  // Free-form payload for callers' custom `filterFn` predicates (e.g. start/end times).
  // The dropdown itself never reads this.
  meta?: unknown;
}

export interface RingilDropdownProps {
  items: RingilDropdownItem[];
  selectedIds: string[];
  onSelectionChange: (selectedIds: string[]) => void;
  readonly?: boolean;
  multiSelect?: boolean;
  selectAllEnabled?: boolean;
  showSelectedIcons?: boolean;
  showSelectedSubLabels?: boolean;
  dataTestIdBase?: string;
  placeholder?: string;
  // Optional UI rendered as the first element in the expanded dropdown panel.
  // Parent owns the state and combines it with `filterFn`. Click events inside
  // are stopped from closing the dropdown.
  filterHeader?: ReactNode;
  // Optional predicate applied to items before the built-in label search filter.
  // Callers use this for custom filtering (range, single-date-in-interval, etc.).
  filterFn?: (item: RingilDropdownItem) => boolean;
  // IDs of items that are mutually exclusive with every other item. Each renders at
  // the top of the list (above "Select all") and is excluded from select-all toggles.
  // Selecting one clears all other selections; selecting any non-exclusive item clears
  // any selected exclusive item. At most one exclusive item can be selected at a time.
  exclusiveItemIds?: string[];
}

const MAX_VISIBLE_CHIPS = 16;

const RingilDropdown: React.FC<RingilDropdownProps> = ({
  items,
  selectedIds,
  onSelectionChange,
  readonly = false,
  multiSelect = true,
  selectAllEnabled = true,
  showSelectedIcons = false,
  showSelectedSubLabels = false,
  dataTestIdBase,
  placeholder,
  filterHeader,
  filterFn,
  exclusiveItemIds,
}) => {
  const { tr } = useTranslationLgs();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownListRef = useRef<HTMLDivElement>(null);

  const listTestId = dataTestIdBase ? `${dataTestIdBase}List` : undefined;
  const overlayTestId = listTestId ? `${listTestId}-overlay` : undefined;

  const isSelected = (id: string) => selectedIds.includes(id);

  const exclusiveIdSet = new Set(exclusiveItemIds ?? []);
  const passesFilters = (item: RingilDropdownItem) => {
    if (filterFn && !filterFn(item)) return false;
    if (!searchTerm) return true;
    return item.label.toLowerCase().includes(searchTerm.toLowerCase());
  };

  // Exclusive items disappear once any non-exclusive item is selected; they come
  // back when the selection is empty (or contains only an exclusive item, so the
  // user can still deselect it).
  const hasNonExclusiveSelected = selectedIds.some(id => !exclusiveIdSet.has(id));
  const filteredExclusiveItems = items.filter(item => exclusiveIdSet.has(item.id) && !hasNonExclusiveSelected && passesFilters(item));
  const filteredRegularItems = items.filter(item => !exclusiveIdSet.has(item.id) && passesFilters(item));
  // Disabled rows stay visible (greyed) but never participate in selection or "Select all".
  const selectableRegularItems = filteredRegularItems.filter(item => !item.disabled);
  // Combined visible list, exclusive items first, used for "no results" detection.
  const filteredItems = [...filteredExclusiveItems, ...filteredRegularItems];

  const selectedItems = items.filter(item => isSelected(item.id));

  const getOptionTestId = (item: RingilDropdownItem, index: number): string | undefined => {
    if (!dataTestIdBase) return undefined;
    const slug = toTestIdSlug(item.label) || toTestIdSlug(item.id) || `item${index}`;
    return `${dataTestIdBase}List-${slug}`;
  };

  const scrollIntoView = (index: number) => {
    const dropdownList = document.querySelector('[data-dropdown-list]');
    const item = document.querySelector(`[data-dropdown-item="${index}"]`);
    if (dropdownList && item) {
      const dropdownRect = dropdownList.getBoundingClientRect();
      const itemRect = item.getBoundingClientRect();
      if (itemRect.top < dropdownRect.top) {
        (dropdownList as HTMLElement).scrollTop -= dropdownRect.top - itemRect.top;
      } else if (itemRect.bottom > dropdownRect.bottom) {
        (dropdownList as HTMLElement).scrollTop += itemRect.bottom - dropdownRect.bottom;
      }
    }
  };

  useEffect(() => {
    if (focusedIndex >= 0) scrollIntoView(focusedIndex);
  }, [focusedIndex]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const insideAnchor = dropdownRef.current && dropdownRef.current.contains(target);
      const insidePortal = dropdownListRef.current && dropdownListRef.current.contains(target);
      if (!insideAnchor && !insidePortal) setIsOpen(false);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  useEffect(() => {
    let rafId: number | null = null;

    const computePosition = () => {
      if (!isOpen || !dropdownRef.current) {
        setDropdownStyle({});
        return;
      }
      const anchorRect = dropdownRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      // Horizontal room is the anchor's band — the content column, or the panel it lives in — not
      // the window: the region beside the content is where side panels live.
      const bounds = getOverlayBounds(dropdownRef.current);
      const margin = 8;
      const preferredMaxHeight = 400;

      let dropdownHeight = preferredMaxHeight;
      if (dropdownListRef.current) {
        dropdownHeight = Math.min(dropdownListRef.current.scrollHeight, preferredMaxHeight);
      }

      const spaceBelow = viewportHeight - anchorRect.bottom;
      const spaceAbove = anchorRect.top;
      const placeBelow = spaceBelow >= Math.min(dropdownHeight, preferredMaxHeight) || spaceBelow >= spaceAbove;

      let top = 0;
      let maxHeight = preferredMaxHeight;
      if (placeBelow) {
        top = Math.max(margin, anchorRect.bottom + window.scrollY);
        maxHeight = Math.min(preferredMaxHeight, Math.max(0, spaceBelow - margin));
      } else {
        const usedHeight = Math.min(preferredMaxHeight, Math.max(0, dropdownHeight));
        top = Math.max(margin, anchorRect.top + window.scrollY - usedHeight);
        maxHeight = usedHeight;
      }

      // Clamped in viewport space, then converted to document coordinates for the absolutely
      // positioned portal.
      const viewportLeft = Math.max(bounds.left + margin, anchorRect.left);
      const left = viewportLeft + window.scrollX;
      const width = Math.min(anchorRect.width, bounds.right - viewportLeft - margin);

      setDropdownStyle({
        position: 'absolute',
        top: Math.round(top),
        left: Math.round(left),
        width: Math.round(width),
        maxHeight: Math.round(maxHeight),
        overflowY: 'auto',
        zIndex: Z_LAYERS.popup,
      });
    };

    computePosition();
    rafId = requestAnimationFrame(computePosition);

    const onResizeScroll = () => computePosition();
    window.addEventListener('resize', onResizeScroll);
    window.addEventListener('scroll', onResizeScroll, true);

    // Track anchor height changes (chip wrap on add/remove) so the panel follows.
    let anchorObserver: ResizeObserver | null = null;
    if (isOpen && dropdownRef.current && typeof ResizeObserver !== 'undefined') {
      anchorObserver = new ResizeObserver(() => computePosition());
      anchorObserver.observe(dropdownRef.current);
    }

    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      window.removeEventListener('resize', onResizeScroll);
      window.removeEventListener('scroll', onResizeScroll, true);
      if (anchorObserver) anchorObserver.disconnect();
    };
  }, [isOpen, filteredItems.length]);

  const commitSelection = (next: string[]) => {
    onSelectionChange(next);
  };

  const toggleItems = (toToggle: RingilDropdownItem[]) => {
    if (toToggle.length === 0) {
      commitSelection([]);
      return;
    }
    // Disabled rows can't be selected — drop them before toggling. If nothing selectable remains, no-op.
    const selectable = toToggle.filter(it => !it.disabled);
    if (selectable.length === 0) return;
    if (!multiSelect) {
      commitSelection([selectable[0].id]);
      setIsOpen(false);
      return;
    }
    const allSelected = selectable.every(it => isSelected(it.id));
    let next: string[];
    if (allSelected) {
      next = selectedIds.filter(id => !selectable.some(it => it.id === id));
    } else {
      const additions = selectable.filter(it => !isSelected(it.id)).map(it => it.id);
      next = [...selectedIds, ...additions];
    }
    if (exclusiveIdSet.size > 0) {
      const newlyAdded = next.filter(id => !selectedIds.includes(id));
      const newExclusives = newlyAdded.filter(id => exclusiveIdSet.has(id));
      const newRegulars = newlyAdded.filter(id => !exclusiveIdSet.has(id));
      if (newExclusives.length > 0) {
        next = [newExclusives[newExclusives.length - 1]];
      } else if (newRegulars.length > 0) {
        next = next.filter(id => !exclusiveIdSet.has(id));
      }
    }
    commitSelection(next);
  };

  const handleChipRemove = (id: string) => {
    commitSelection(selectedIds.filter(s => s !== id));
    setSearchTerm('');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    if (!isOpen) setIsOpen(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (readonly) return;

    if (e.key === 'Backspace' && searchTerm === '' && selectedItems.length > 0) {
      e.preventDefault();
      handleChipRemove(selectedItems[selectedItems.length - 1].id);
      return;
    }

    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true);
        setFocusedIndex(0);
        e.preventDefault();
      }
      return;
    }

    const exclusiveCount = filteredExclusiveItems.length;
    const showSelectAll = selectAllEnabled && selectableRegularItems.length > 0;
    const selectAllOffset = showSelectAll ? 1 : 0;
    const regularBaseIndex = exclusiveCount + selectAllOffset;
    const totalFocusable = exclusiveCount + selectAllOffset + filteredRegularItems.length;
    const maxFocus = Math.max(0, totalFocusable - 1);

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex(prev => (prev < maxFocus ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex(prev => (prev > 0 ? prev - 1 : prev));
        break;
      case 'Enter':
        e.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < exclusiveCount) {
          // focusedIndex is -1 when nothing is focused (e.g. dropdown opened by typing). Without the
          // >= 0 guard, filteredExclusiveItems[-1] is undefined and toggleItems derefs it.id. (Sentry R5.)
          toggleItems([filteredExclusiveItems[focusedIndex]]);
        } else if (showSelectAll && focusedIndex === exclusiveCount) {
          const allSelected = selectableRegularItems.length > 0 && selectableRegularItems.every(it => isSelected(it.id));
          toggleItems(allSelected ? [] : selectableRegularItems);
        } else {
          const itemIdx = focusedIndex - regularBaseIndex;
          if (itemIdx >= 0 && itemIdx < filteredRegularItems.length) {
            toggleItems([filteredRegularItems[itemIdx]]);
          }
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setFocusedIndex(-1);
        break;
    }
  };

  const renderAllItem = (isFocused: boolean) => {
    const allSelected = selectableRegularItems.length > 0 && selectableRegularItems.every(it => isSelected(it.id));
    return (
      <div
        className={clsx(
          'flex flex-row py-1.5 px-3.5 items-center gap-4 self-stretch h-11 hover:bg-content-quaternary',
          isFocused
            ? 'bg-content-quaternary'
            : allSelected
              ? 'bg-content-brandMedium'
              : 'bg-content-negativePrimary',
        )}
      >
        <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
          <div className="flex flex-row self-stretch text-content-primary font-['DM_Sans'] text-sm font-normal leading-normal overflow-hidden text-ellipsis whitespace-nowrap">
            {allSelected
              ? tr('RingilDropdown.deselectAll', 'Deselect all {{count}}', { count: selectableRegularItems.length })
              : tr('RingilDropdown.selectAll', 'Select all {{count}}', { count: selectableRegularItems.length })}
          </div>
        </div>
      </div>
    );
  };

  const renderItem = (item: RingilDropdownItem, isFocused: boolean) => {
    const selected = isSelected(item.id);
    const disabled = !!item.disabled;
    return (
      <div
        className={clsx(
          'flex flex-row py-1.5 items-center gap-4 self-stretch px-2',
          disabled ? 'opacity-70' : 'hover:bg-content-quaternary',
          isFocused && !disabled
            ? 'bg-content-quaternary'
            : selected
              ? 'bg-content-brandMedium'
              : 'bg-content-negativePrimary',
        )}
      >
        <div
          className={clsx(
            'flex justify-center items-center rounded-full bg-content-quaternary border border-solid w-[34px] h-[34px]',
            disabled ? 'text-content-tertiary' : 'text-content-secondary',
          )}
        >
          {selected ? <SvgSelected className="text-content-brand w-6 h-6" /> : item.icon}
        </div>
        <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
          <div
            className={clsx(
              "flex flex-row self-stretch font-['DM_Sans'] text-sm font-normal leading-normal overflow-hidden text-ellipsis whitespace-nowrap items-center",
              disabled ? 'text-content-tertiary' : 'text-content-primary',
            )}
          >
            {item.labelNode ?? item.label}
          </div>
          {item.subLabel != null && (
            <div
              className={clsx(
                "font-['DM_Sans'] text-xs font-normal leading-normal tracking-[0.24px] overflow-hidden text-ellipsis whitespace-nowrap",
                disabled ? 'text-content-tertiary' : 'text-content-secondary',
              )}
            >
              <div className="flex flex-row flex-1">{item.subLabel}</div>
            </div>
          )}
          {disabled && item.disabledReason != null && (
            <div className="text-complementary-error font-['DM_Sans'] text-xs font-normal leading-normal tracking-[0.24px] whitespace-normal">
              {item.disabledReason}
            </div>
          )}
        </div>
      </div>
    );
  };

  const dropdownElement = (
    <div
      ref={dropdownListRef}
      className="flex flex-col gap-0.5 bg-content-quaternary border border-[var(--base-basesecondary)] border-t-0 rounded-b-[4px] z-900 shadow-[0_4px_8px_rgba(0,0,0,0.1)]"
      data-dropdown-list
      data-testid={overlayTestId}
      style={dropdownStyle}
    >
      {filterHeader && (
        <div
          className="sticky top-0 z-10 px-2 py-2 border-b border-solid border-[var(--base-basesecondary)] bg-white"
          data-testid={dataTestIdBase ? `${dataTestIdBase}List-filterHeader` : undefined}
          onClick={e => e.stopPropagation()}
        >
          {filterHeader}
        </div>
      )}
      {filteredItems.length > 0 ? (
        <>
          {filteredExclusiveItems.map((item, index) => {
            const itemFocusIndex = index;
            return (
              <div
                key={item.id}
                data-dropdown-item={itemFocusIndex}
                data-testid={getOptionTestId(item, index)}
                className="cursor-pointer"
                onClick={() => toggleItems([item])}
              >
                {renderItem(item, itemFocusIndex === focusedIndex)}
              </div>
            );
          })}
          {selectAllEnabled && selectableRegularItems.length > 0 && (
            <div
              key="all"
              data-dropdown-item={filteredExclusiveItems.length}
              data-testid={dataTestIdBase ? `${dataTestIdBase}List-selectAll` : undefined}
              className="cursor-pointer"
              onClick={() => {
                const allSelected = selectableRegularItems.length > 0 && selectableRegularItems.every(it => isSelected(it.id));
                toggleItems(allSelected ? [] : selectableRegularItems);
              }}
            >
              {renderAllItem(focusedIndex === filteredExclusiveItems.length)}
            </div>
          )}
          {filteredRegularItems.map((item, index) => {
            const selectAllOffset = selectAllEnabled && selectableRegularItems.length > 0 ? 1 : 0;
            const itemFocusIndex = filteredExclusiveItems.length + selectAllOffset + index;
            return (
              <div
                key={item.id}
                data-dropdown-item={itemFocusIndex}
                data-testid={getOptionTestId(item, filteredExclusiveItems.length + index)}
                className={item.disabled ? 'cursor-not-allowed' : 'cursor-pointer'}
                onClick={() => {
                  if (!item.disabled) toggleItems([item]);
                }}
              >
                {renderItem(item, itemFocusIndex === focusedIndex)}
              </div>
            );
          })}
        </>
      ) : (
        <div className="p-3 text-content-secondary text-center italic">
          {tr('RingilDropdown.noResults', 'No items found')}
        </div>
      )}
    </div>
  );

  return (
    <div ref={dropdownRef} className="relative w-full">
      <div className={clsx('r-input-wrapper flex items-center border border-solid border-content-secondary bg-white rounded', readonly && 'border-content-quaternary')}>
        <div className="flex gap-2 p-1 items-center justify-between w-full min-h-[38px]">
          <div className="flex flex-wrap flex-1 gap-2 items-center">
            {selectedItems.slice(0, Math.min(selectedItems.length, MAX_VISIBLE_CHIPS)).map(item => {
              const showSubLabel = showSelectedSubLabels && item.subLabel != null;
              return (
                <div
                  key={item.id}
                  className={clsx(
                    'flex py-1.5 px-1 justify-end items-center gap-2 rounded border border-solid border-content-secondary bg-white',
                    !showSubLabel && 'h-5',
                  )}
                >
                  {showSelectedIcons && item.icon && (
                    <span className="inline-flex items-center shrink-0">{item.icon}</span>
                  )}
                  <div className="flex flex-col min-w-0">
                    <span className="text-content-primary font-['DM_Sans'] text-[12px] font-normal leading-normal tracking-[0.24px] overflow-hidden text-ellipsis whitespace-nowrap">
                      {item.label}
                    </span>
                    {showSubLabel && (
                      <span className="text-content-secondary font-['DM_Sans'] text-[10px] font-normal leading-normal tracking-[0.2px] overflow-hidden text-ellipsis whitespace-nowrap">
                        {item.subLabel}
                      </span>
                    )}
                  </div>
                  <div
                    onClick={e => {
                      e.stopPropagation();
                      handleChipRemove(item.id);
                    }}
                  >
                    <SvgClose className="text-content-brand cursor-pointer transition-all duration-200 hover:text-complementary-brandDark" />
                  </div>
                </div>
              );
            })}
            {selectedItems.length > MAX_VISIBLE_CHIPS && (
              <div
                className="flex flex-row h-5 py-1.5 px-1 justify-end items-center gap-1 rounded cursor-pointer"
                onClick={() => {
                  if (!readonly) setIsOpen(!isOpen);
                }}
              >
                <SvgPlus className="text-content-brand cursor-pointer w-2.5 h-2.5 transition-all duration-200 hover:text-complementary-brandDark" />
                <span className="text-content-brand font-['DM_Sans'] text-[12px] font-normal leading-normal tracking-[0.24px]">
                  {tr('RingilDropdown.overflow', '{{count}} items', { count: selectedItems.length - MAX_VISIBLE_CHIPS })}
                </span>
              </div>
            )}
            {(multiSelect || selectedItems.length === 0) && (
              <input
                className="p-no-border min-w-52 flex-1 p-2 font-sans text-[15px] outline-none bg-transparent"
                type="text"
                data-testid={listTestId}
                value={searchTerm}
                onClick={() => {
                  if (!readonly) setIsOpen(prev => !prev);
                }}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder={placeholder ?? tr('RingilDropdown.placeholder', 'Search...')}
              />
            )}
          </div>
          <div className="flex flex-row items-center justify-end">
            <SvgChevron
              className={clsx(
                'w-[12px] h-[12px] flex-shrink-0 mx-2 transition-transform cursor-pointer',
                isOpen ? 'rotate-180' : '',
                !readonly ? 'cursor-pointer' : 'cursor-not-allowed text-content-quaternary',
              )}
              onClick={() => {
                if (!readonly) setIsOpen(prev => !prev);
              }}
            />
          </div>
        </div>
      </div>
      {isOpen && createPortal(dropdownElement, document.body)}
    </div>
  );
};

export default RingilDropdown;